import { Form, Formik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/all';
import { useDispatch, useSelector } from 'react-redux';
import {
	Address,
	AddressChange,
	FamilyMember,
	FamilyMembersNewAddress,
	IFamilyMembersService,
} from '../../../../@posturinn/client-react';
import { closeAllModals } from '../../../../actions/modalActions';
import {
	updateUserAddressError,
	updateUserAddressFulfilled,
	updateUserAddressPending,
} from '../../../../actions/userProfileActions';
import useFamilyMembers from '../../../../hooks/useFamilyMembers';
import { ReactComponent as Close } from '../../../../icons/close-button.svg';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import Button from '../../../base/button';
import { StyledCheckbox } from '../../../base/forms/checkbox';
import { StyledCheckboxLabel, StyledCheckboxWrapper } from '../../../base/forms/formikCheckbox';
import FormikFieldWrapper from '../../../base/forms/formikFieldWrapper';
import { StyledErrorMessage } from '../../../base/forms/formikFieldWrapper.styles';
import FormikSelect from '../../../base/forms/formikSelect';
import { StyledFormikField } from '../../../base/forms/input';
import { Label } from '../../../base/forms/label';
import { A } from '../../../base/typography';
import * as StyledModals from '../myShipmentsModals.styles';
import { changeAddressInitialValues, changeAddressSchema } from './changeAddress.schema';

interface Props {}

const ChangeAddress: FC<Props> = () => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile, modalSubmitError, modalSubmitErrorDetails } = userProfileReducer;

	const [addresses, setAddresses]: [any[], (x: any[]) => void] = useState([]);
	const [errorFetchingAddresses, setErrorFetchingAddresses] = useState('');
	const [addressInput, setAddressInput] = useState('');
	const [addressInputFetched, setAddressInputFetched] = useState('');
	const { family } = useFamilyMembers();
	const { injector } = useSelector(({ mobizReducer }) => mobizReducer);
	const familyMembersService: IFamilyMembersService = injector.get(IFamilyMembersService);

	const getPostalCodesForAddress = (address: string) => {
		if (addressInput === addressInputFetched) return;
		setAddressInputFetched(address);
		if (address.length > 2) {
			familyMembersService.getPostalCodesForAddress(address).subscribe({
				next: (addresses: Address[]) => {
					setErrorFetchingAddresses('');
					setAddresses(addresses);
				},
				error: (response): void => {
					setErrorFetchingAddresses(response.info.details);
					setAddresses([]);
				},
			});
		}
	};

	useEffect(() => {
		const timeoutId = setTimeout(() => getPostalCodesForAddress(addressInput), 1000);
		return () => clearTimeout(timeoutId);
	});

	const closeModal = () => {
		dispatch(closeAllModals());
		document.body.style.overflow = 'unset';
	};

	const onSubmit = (values, { setSubmitting }) => {
		dispatch(updateUserAddressPending());
		const addressChange = new AddressChange({
			streetName: addressInput,
			streetNumber: values.streetNumber,
			apartmentNumber: values.appartmentNumber,
			postalCode: values.zip.value,
			city: values.zip.label.split(' ')[1],
			additionalInformation: values.additionalInformation,
		});
		const updateFamilyObject = new FamilyMembersNewAddress({
			addressChange: addressChange,
			familyMembersToChangeAddressFor:
				family.familyMembers.length > 1
					? values.familyMembers
					: [
							{
								name: userProfile.name,
								identificationNumber: userProfile.identificationNumber,
							},
					  ],
		});

		familyMembersService.updateFamilyMembersAddress(updateFamilyObject).subscribe({
			next: newUserProfile => {
				setSubmitting(false);
				dispatch(updateUserAddressFulfilled(newUserProfile));
				closeModal();
			},
			error: error => {
				let err = '';
				try {
					err = error.info.details;
				} catch (e) {
					// empty
				}
				setSubmitting(false);
				dispatch(updateUserAddressError(err));
			},
		});
	};
	document.body.style.overflow = 'hidden';
	return (
		<StyledModals.Wrapper>
			<StyledModals.Heading>
				<div>{t('page-settings.changeAddress')}</div>
				<A onClick={closeModal}>
					<Close />
				</A>
			</StyledModals.Heading>
			{(modalSubmitError || errorFetchingAddresses !== '') && (
				<StyledModals.ErrorMessage>
					<FaExclamationCircle />
					{/* eslint-disable-next-line no-nested-ternary */}
					<div>
						{errorFetchingAddresses !== ''
							? errorFetchingAddresses
							: modalSubmitErrorDetails !== ''
							? modalSubmitErrorDetails
							: t('genericError')}
					</div>
				</StyledModals.ErrorMessage>
			)}

			{addresses.length === 0 && addressInput.length > 2 && (
				<>
					<StyledErrorMessage>{t('errorFetchingPostalCodes') + ' ' + addressInput}</StyledErrorMessage>
					<StyledErrorMessage>{t('errorFetchingPostalCodesInfo')}</StyledErrorMessage>
				</>
			)}
			<Formik
				initialValues={changeAddressInitialValues}
				validationSchema={changeAddressSchema}
				onSubmit={onSubmit}>
				{({ setFieldValue, setFieldTouched, isSubmitting, values, errors }) => {
					const selectOrDeselectMember = (member, isSelected) => {
						if (isSelected) {
							const index = values.familyMembers.indexOf(member);
							if (index > -1) {
								values.familyMembers.splice(index, 1);
								setFieldValue('familyMembers', values.familyMembers);
							}
						} else {
							const { familyMembers } = values;
							setFieldValue('familyMembers', [...familyMembers, member]);
						}
					};
					const postcodeError = errors.zip;
					const checkboxError = errors.familyMembers;
					return (
						<Form>
							<FormikFieldWrapper
								name="street"
								smallLabel
								label={`${t('street')}*`}
								languageComponent="myShipments">
								<StyledFormikField
									type="text"
									id="street"
									name="street"
									onChange={e => {
										const next = e.target.value;
										setAddressInput(next);
										setFieldValue('street', next);
									}}
								/>
							</FormikFieldWrapper>
							<FormikFieldWrapper
								name="streetNumber"
								label={`${t('streetNumber')}*`}
								smallLabel
								languageComponent="myShipments">
								<StyledFormikField
									type="text"
									id="streetNumber"
									name="streetNumber"
									disabled={addresses.length === 0}
								/>
							</FormikFieldWrapper>
							<div style={{ marginBottom: '1rem' }}>
								<Label small>{`${t('postCode')}*`}</Label>
								<FormikSelect
									id={'zip'}
									name="zip"
									options={addresses.map(e => {
										return {
											value: e.postalCode,
											label: `${e.postalCode} ${e.city}`,
										};
									})}
									placeholder={t('page-settings.zip')}
									onBlur={setFieldTouched}
									onChange={setFieldValue}
									autocomplete="postal-code"
									value={values.zip}
									disabled={addresses.length === 0}
								/>
								{postcodeError && <StyledErrorMessage>{t('zip-required')}</StyledErrorMessage>}
							</div>
							<FormikFieldWrapper
								name="appartmentNumber"
								label={t('appartmentNumber')}
								smallLabel
								languageComponent="myShipments">
								<StyledFormikField
									type="text"
									id="appartmentNumber"
									name="appartmentNumber"
									disabled={addresses.length === 0}
								/>
							</FormikFieldWrapper>
							<FormikFieldWrapper
								name="additionalInformation"
								label={t('additionalInformation')}
								smallLabel
								languageComponent="myShipments">
								<StyledFormikField
									type="text"
									id="additionalInformation"
									name="additionalInformation"
									disabled={addresses.length === 0}
								/>
							</FormikFieldWrapper>
							{family?.familyMembers.length > 0 && (
								<StyledModals.FamilyMembersWrapper hide={family?.familyMembers.length < 2}>
									<span>{`${t('changeAddressForPeople')}*`}</span>
									<StyledModals.CheckboxWrapper>
										{family?.familyMembers.map((familyMember: FamilyMember) => {
											let checked = false;
											if (family.familyMembers.length < 2) {
												values.familyMembers.push(familyMember);
												checked = true;
											}
											if (
												values.familyMembers?.find(
													e => e.identificationNumber === familyMember.identificationNumber
												)
											) {
												checked = true;
											}
											return (
												<StyledCheckboxWrapper key={familyMember.identificationNumber}>
													<StyledCheckboxLabel>
														<StyledCheckbox
															checked={checked}
															onClick={() =>
																selectOrDeselectMember(familyMember, checked)
															}
															tabIndex="0"
															id={familyMember.identificationNumber}
														/>
														<span>{familyMember.name}</span>
													</StyledCheckboxLabel>
												</StyledCheckboxWrapper>
											);
										})}
									</StyledModals.CheckboxWrapper>
								</StyledModals.FamilyMembersWrapper>
							)}
							{checkboxError && (
								<StyledErrorMessage>{t('familyMembers-one-required')}</StyledErrorMessage>
							)}

							<Button primary type="submit" isLoading={isSubmitting}>
								{t('save')}
							</Button>
						</Form>
					);
				}}
			</Formik>
		</StyledModals.Wrapper>
	);
};

export default ChangeAddress;
